import { create } from "zustand";

export interface ZoneDefinition {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  isHandwriting?: boolean;
  /**
   * Marksense v2 — declares the zone as a mark-detection zone. Mirrors
   * `FieldContent.zoneDefinition.markKind` (the save path writes the zone
   * object wholesale). Authoring note surfaced in the props strip: circled
   * answers need `AnyMark`, not `Checkbox` (frame suppression can erase a
   * drawn ring with the printed box outline).
   */
  markKind?: 'Checkbox' | 'Bubble' | 'Signature' | 'AnyMark';
  /** Marksense v2 — membership in a `FormContent.markGroups` declaration. */
  markGroupId?: string;
}

export interface OcrSuggestion {
  id: string;
  fieldName: string;
  value: string;
  confidence: number;
  boundingBox: ZoneDefinition;
  status: "pending" | "linked" | "rejected";
  linkedFieldId?: string;
}

export interface ZoneMapping {
  fieldDefinitionId: string;
  fieldName: string;
  fieldLabel: string;
  /**
   * R-125 (5.7) — Whether this field is part of the caller-driven OCR
   * extraction subset. Drives the zone class: OCR fields get the copper
   * accent on the canvas, guide-only (non-OCR) fields get the muted slate
   * color. Class is derived from this flag, not stored on the zone itself.
   */
  ocrExtracted: boolean;
  zone: ZoneDefinition | null;
  isDirty: boolean;
}

export interface TemplatePageState {
  pageNumber: number;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  anchorText: string | null;
  anchorDirty: boolean;
}

export type ToolMode = "select" | "draw" | "pan";

export interface Viewport {
  x: number;
  y: number;
  scale: number;
}

export interface DrawingRect {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface ZoneEditorState {
  // Template metadata
  templateId: string | null;
  pageMatchingStrategy: string;

  // Pages
  pages: TemplatePageState[];
  currentPageNumber: number;

  // Zone mappings
  zoneMappings: ZoneMapping[];
  selectedZoneFieldId: string | null;

  // OCR suggestions
  ocrSuggestions: OcrSuggestion[];
  showDismissed: boolean;

  // Viewport / drawing
  viewport: Viewport;
  toolMode: ToolMode;
  drawingRect: DrawingRect | null;
  panelCollapsed: boolean;

  // Status
  isDirty: boolean;
  isLoading: boolean;

  // Actions
  loadTemplate(data: {
    templateId: string;
    pageMatchingStrategy: string;
    pages: TemplatePageState[];
    zoneMappings: ZoneMapping[];
  }): void;
  /**
   * R-125 (5.4 + 5.5) — Reconcile the zoneMappings list with a fresh
   * fieldDefinitions snapshot from the form-config draft. Existing zones
   * stay attached to their fieldDefinitionId; labels / ocrExtracted update
   * in place; new fields appear with their server-side `zoneDefinition`
   * (or null); removed fields drop. Does NOT touch `isDirty` flags — this
   * is a pure re-sync, not a user edit.
   *
   * R-175 — `zoneDefinition` is the server's persisted zone for the field.
   * Required so a publish that allocates new `FieldDefinition.id`s (which
   * miss the in-memory `byId` lookup) doesn't silently drop their zones to
   * null. Existing entries keep their in-memory zone — operator drafts win
   * over server state.
   */
  syncFieldDefinitions(
    fieldDefinitions: Array<{
      fieldDefinitionId: string;
      name: string;
      label: string;
      ocrExtracted: boolean;
      zoneDefinition?: ZoneDefinition | null;
    }>,
  ): void;
  setCurrentPage(pageNumber: number): void;
  setToolMode(mode: ToolMode): void;
  setViewport(partial: Partial<Viewport>): void;
  selectZone(fieldId: string | null): void;
  /**
   * R-125 (5.6) — Click a field row in the right panel to enter drawing
   * mode for that field directly (no separate Draw toolbar toggle). The
   * next mousedown+drag on the canvas creates the zone for this field.
   */
  selectFieldForDraw(fieldId: string): void;
  startDraw(x: number, y: number): void;
  updateDraw(x: number, y: number): void;
  finishDraw(fieldDefinitionId: string): void;
  cancelDraw(): void;
  updateZone(fieldId: string, zone: ZoneDefinition): void;
  /**
   * Marksense v2 — merge mark/handwriting props into a field's EXISTING zone
   * (no-op when no zone is drawn). Passing `undefined` for a key clears it.
   * Rides the wholesale zone save path — no separate persistence.
   */
  updateZoneProps(
    fieldId: string,
    props: Partial<Pick<ZoneDefinition, 'isHandwriting' | 'markKind' | 'markGroupId'>>,
  ): void;
  removeZone(fieldId: string): void;
  linkSuggestion(suggestionId: string, fieldDefinitionId: string): void;
  rejectSuggestion(suggestionId: string): void;
  toggleShowDismissed(): void;
  setOcrSuggestions(suggestions: OcrSuggestion[]): void;
  updateAnchorText(pageNumber: number, text: string): void;
  setPageMatchingStrategy(strategy: string): void;
  togglePanel(): void;
  markClean(): void;
  reset(): void;
}

const defaultViewport: Viewport = { x: 0, y: 0, scale: 1 };

export const useZoneEditorStore = create<ZoneEditorState>((set, get) => ({
  // Initial state
  templateId: null,
  pageMatchingStrategy: "page_number",
  pages: [],
  currentPageNumber: 1,
  zoneMappings: [],
  selectedZoneFieldId: null,
  ocrSuggestions: [],
  showDismissed: false,
  viewport: defaultViewport,
  toolMode: "select",
  drawingRect: null,
  panelCollapsed: false,
  isDirty: false,
  isLoading: false,

  // Actions
  loadTemplate(data) {
    set((state) => {
      // F-014 — reloading the SAME template (e.g. the autosave-driven
      // re-derive that fires ~600ms after a draw) must NOT reset the
      // operator's zoom/pan back to default; that forced re-zooming for
      // every zone. Only a genuinely different template resets the viewport
      // (and current page). The canvas's per-url fit-to-page still handles
      // the very first load via its own ref guard.
      const sameTemplate = state.templateId === data.templateId;
      return {
        templateId: data.templateId,
        pageMatchingStrategy: data.pageMatchingStrategy,
        pages: data.pages,
        zoneMappings: data.zoneMappings,
        currentPageNumber: sameTemplate
          ? state.currentPageNumber
          : (data.pages[0]?.pageNumber ?? 1),
        selectedZoneFieldId: null,
        ocrSuggestions: [],
        viewport: sameTemplate ? state.viewport : defaultViewport,
        toolMode: "select",
        drawingRect: null,
        isDirty: false,
        isLoading: false,
      };
    });
  },

  syncFieldDefinitions(fieldDefinitions) {
    set((state) => {
      const byId = new Map(state.zoneMappings.map((zm) => [zm.fieldDefinitionId, zm]));
      const nextMappings: ZoneMapping[] = fieldDefinitions.map((fd) => {
        const existing = byId.get(fd.fieldDefinitionId);
        if (existing) {
          return {
            ...existing,
            fieldName: fd.name,
            fieldLabel: fd.label,
            ocrExtracted: fd.ocrExtracted,
          };
        }
        return {
          fieldDefinitionId: fd.fieldDefinitionId,
          fieldName: fd.name,
          fieldLabel: fd.label,
          ocrExtracted: fd.ocrExtracted,
          // R-175 — seed new entries from the server-side zoneDefinition so
          // a publish that mints new FieldDefinition.id values (which miss
          // the byId lookup above) doesn't wipe their zones from the UI.
          zone: fd.zoneDefinition ?? null,
          isDirty: false,
        };
      });
      // Keep the selection if the field still exists; otherwise drop it.
      const stillExists = nextMappings.some(
        (zm) => zm.fieldDefinitionId === state.selectedZoneFieldId,
      );
      return {
        zoneMappings: nextMappings,
        selectedZoneFieldId: stillExists ? state.selectedZoneFieldId : null,
      };
    });
  },

  setCurrentPage(pageNumber) {
    set({ currentPageNumber: pageNumber });
  },

  setToolMode(mode) {
    set({ toolMode: mode });
  },

  setViewport(partial) {
    set((state) => ({ viewport: { ...state.viewport, ...partial } }));
  },

  selectZone(fieldId) {
    set({ selectedZoneFieldId: fieldId, toolMode: "select" });
  },

  selectFieldForDraw(fieldId) {
    // R-125 (5.6) — Picking a field row in the right panel enters draw mode
    // for that field. The next mousedown+drag on the canvas creates the zone.
    set({ selectedZoneFieldId: fieldId, toolMode: "draw", drawingRect: null });
  },

  startDraw(x, y) {
    set({ drawingRect: { startX: x, startY: y, currentX: x, currentY: y } });
  },

  updateDraw(x, y) {
    set((state) => {
      if (!state.drawingRect) return state;
      return { drawingRect: { ...state.drawingRect, currentX: x, currentY: y } };
    });
  },

  finishDraw(fieldDefinitionId) {
    const { drawingRect, currentPageNumber } = get();
    if (!drawingRect) return;

    const x = Math.min(drawingRect.startX, drawingRect.currentX);
    const y = Math.min(drawingRect.startY, drawingRect.currentY);
    const width = Math.abs(drawingRect.currentX - drawingRect.startX);
    const height = Math.abs(drawingRect.currentY - drawingRect.startY);

    // R-158 — drawing coords are NORMALIZED to 0–1 (see canvasToPage in
    // zone-math.ts), so the prior `< 2` threshold rejected EVERY drag
    // (no zone could ever span 2x the page width). Use 0.005 (0.5% of the
    // page in each axis) to match the legacy onZoneDrawn callback path,
    // which filters tiny accidental clicks without blocking real draws.
    if (width < 0.005 || height < 0.005) {
      set({ drawingRect: null, toolMode: "select" });
      return;
    }

    const zone: ZoneDefinition = { pageNumber: currentPageNumber, x, y, width, height };

    set((state) => ({
      zoneMappings: state.zoneMappings.map((zm) =>
        zm.fieldDefinitionId === fieldDefinitionId
          ? { ...zm, zone, isDirty: true }
          : zm
      ),
      selectedZoneFieldId: fieldDefinitionId,
      drawingRect: null,
      toolMode: "select",
      isDirty: true,
    }));
  },

  cancelDraw() {
    set({ drawingRect: null });
  },

  updateZone(fieldId, zone) {
    set((state) => ({
      zoneMappings: state.zoneMappings.map((zm) =>
        zm.fieldDefinitionId === fieldId ? { ...zm, zone, isDirty: true } : zm
      ),
      isDirty: true,
    }));
  },

  updateZoneProps(fieldId, props) {
    set((state) => {
      const target = state.zoneMappings.find((zm) => zm.fieldDefinitionId === fieldId);
      if (!target?.zone) return state; // props live ON the zone — nothing drawn, nothing to set
      const zone: ZoneDefinition = { ...target.zone, ...props };
      // `undefined` means CLEAR the prop — strip such keys so the persisted
      // zone JSON stays compact (and `markKind in zone` checks stay honest).
      for (const key of Object.keys(props) as Array<keyof typeof props>) {
        if (props[key] === undefined) delete zone[key];
      }
      return {
        zoneMappings: state.zoneMappings.map((zm) =>
          zm.fieldDefinitionId === fieldId ? { ...zm, zone, isDirty: true } : zm,
        ),
        isDirty: true,
      };
    });
  },

  removeZone(fieldId) {
    set((state) => ({
      zoneMappings: state.zoneMappings.map((zm) =>
        zm.fieldDefinitionId === fieldId ? { ...zm, zone: null, isDirty: true } : zm
      ),
      isDirty: true,
    }));
  },

  linkSuggestion(suggestionId, fieldDefinitionId) {
    const suggestion = get().ocrSuggestions.find((s) => s.id === suggestionId);
    if (!suggestion) return;

    set((state) => ({
      ocrSuggestions: state.ocrSuggestions.map((s) =>
        s.id === suggestionId
          ? { ...s, status: "linked" as const, linkedFieldId: fieldDefinitionId }
          : s
      ),
      zoneMappings: state.zoneMappings.map((zm) =>
        zm.fieldDefinitionId === fieldDefinitionId
          ? { ...zm, zone: suggestion.boundingBox, isDirty: true }
          : zm
      ),
      isDirty: true,
    }));
  },

  rejectSuggestion(suggestionId) {
    set((state) => ({
      ocrSuggestions: state.ocrSuggestions.map((s) =>
        s.id === suggestionId ? { ...s, status: "rejected" as const } : s
      ),
    }));
  },

  toggleShowDismissed() {
    set((state) => ({ showDismissed: !state.showDismissed }));
  },

  setOcrSuggestions(suggestions) {
    set({ ocrSuggestions: suggestions });
  },

  updateAnchorText(pageNumber, text) {
    set((state) => ({
      pages: state.pages.map((p) =>
        p.pageNumber === pageNumber ? { ...p, anchorText: text, anchorDirty: true } : p
      ),
      isDirty: true,
    }));
  },

  setPageMatchingStrategy(strategy) {
    set({ pageMatchingStrategy: strategy, isDirty: true });
  },

  togglePanel() {
    set((state) => ({ panelCollapsed: !state.panelCollapsed }));
  },

  markClean() {
    set({
      isDirty: false,
      zoneMappings: get().zoneMappings.map((zm) => ({ ...zm, isDirty: false })),
      pages: get().pages.map((p) => ({ ...p, anchorDirty: false })),
    });
  },

  reset() {
    set({
      templateId: null,
      pageMatchingStrategy: "page_number",
      pages: [],
      currentPageNumber: 1,
      zoneMappings: [],
      selectedZoneFieldId: null,
      ocrSuggestions: [],
      showDismissed: false,
      viewport: defaultViewport,
      toolMode: "select",
      drawingRect: null,
      panelCollapsed: false,
      isDirty: false,
      isLoading: false,
    });
  },
}));
